import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { useGetTransactionByIdQuery } from '../../../store/api/transactionsApi';
import type { Transaction } from '../../../types/api';
import { formatAmountExact } from '../../../utils/amountFormatter';
import { formatDate, formatRelativeTime } from '../../../utils/dateFormatter';

export default function TransactionDetailsPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const currentLanguage = (i18n.language || 'ar') as 'ar' | 'en';

  const transactionId = id || '';

  const { data, isLoading, error } = useGetTransactionByIdQuery(transactionId, {
    skip: !transactionId,
  });

  const transaction: Transaction | undefined = data?.data?.transaction;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !transaction) {
    return (
      <div className="text-center py-12">
        <p className="text-danger mb-4">
          {t('admin.transactions.details.error.title') || 'Error loading transaction'}
        </p>
        <p className="text-neutral-medium mb-6">
          {t('admin.transactions.details.error.notFound') || 'Transaction not found'}
        </p>
        <Link
          to="/admin/transactions"
          className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-colors duration-200"
        >
          {t('admin.transactions.details.back') || 'Back to Transactions'}
        </Link>
      </div>
    );
  }

  const getStatusColor = (transactionStatus: string) => {
    switch (transactionStatus) {
      case 'completed':
        return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      case 'failed':
        return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'reversed':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
      default:
        return 'bg-neutral-medium/20 text-neutral-medium border-neutral-medium/50';
    }
  };

  const getTypeColor = (transactionType: string) => {
    switch (transactionType) {
      case 'recharge':
      case 'admin_add':
      case 'referral':
        return 'text-brand-luxury drop-shadow-[0_0_3px_rgba(230,161,87,0.2)]';
      case 'debit':
      case 'admin_deduct':
        return 'text-red-400';
      case 'refund':
        return 'text-orange-400';
      default:
        return 'text-neutral-light';
    }
  };

  const getTypeSign = (transactionType: string) => {
    switch (transactionType) {
      case 'recharge':
      case 'admin_add':
      case 'referral':
        return '+';
      case 'debit':
      case 'admin_deduct':
        return '-';
      case 'refund':
        return '↩';
      default:
        return '';
    }
  };

  return (
    <div className="px-2 sm:px-4 lg:px-0">
      {/* Header */}
      <div
        className="mb-6 sm:mb-8"
        style={{
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        <Link
          to="/admin/transactions"
          className="text-brand-accent hover:text-brand-accent/80 font-semibold mb-4 inline-flex items-center gap-2 transition-all duration-200 hover:underline text-sm sm:text-base"
        >
          <svg
            className="w-4 h-4 sm:w-5 sm:h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t('admin.transactions.details.back') || 'Back to Transactions'}
        </Link>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-brand-accent mb-2">
          {t('admin.transactions.details.title') || 'Transaction Details'}
        </h1>
        <p className="text-sm sm:text-base text-neutral-medium">
          {t('admin.transactions.details.info.transactionId') || 'Transaction ID'}: {transaction._id.slice(-8)}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Main Info Card */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* Transaction Information */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 sm:p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.1s both',
            }}
          >
            <h2 className="text-lg sm:text-xl font-heading font-bold text-brand-accent mb-4 sm:mb-6">
              {t('admin.transactions.details.info.title') || 'Transaction Information'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.transactions.details.info.transactionId') || 'Transaction ID'}
                </label>
                <p className="text-neutral-light font-mono text-sm sm:text-base">{transaction._id}</p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.transactions.details.info.user') || 'User'}
                </label>
                <Link
                  to={`/admin/users/${transaction.userId}`}
                  className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-colors duration-200 hover:underline text-sm sm:text-base"
                >
                  {transaction.userId}
                </Link>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.transactions.details.info.type') || 'Type'}
                </label>
                <p className={`text-sm sm:text-base capitalize ${getTypeColor(transaction.type)}`}>
                  {getTypeSign(transaction.type)}{' '}
                  {t(`admin.transactions.types.${transaction.type}`) || transaction.type}
                </p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.transactions.details.info.amount') || 'Amount'}
                </label>
                <p className={`text-lg sm:text-xl font-semibold ${getTypeColor(transaction.type)}`}>
                  {getTypeSign(transaction.type)}${formatAmountExact(transaction.amount)} {transaction.currency}
                </p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.transactions.details.info.currency') || 'Currency'}
                </label>
                <p className="text-neutral-light text-sm sm:text-base uppercase">{transaction.currency}</p>
              </div>
              {transaction.method && (
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                    {t('admin.transactions.details.info.method') || 'Payment Method'}
                  </label>
                  <p className="text-neutral-light text-sm sm:text-base">{transaction.method}</p>
                </div>
              )}
              {transaction.reference && (
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                    {t('admin.transactions.details.info.reference') || 'Reference'}
                  </label>
                  <p className="text-neutral-light font-mono text-xs sm:text-sm break-all">
                    {transaction.reference}
                  </p>
                </div>
              )}
              {transaction.notes && (
                <div className="sm:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                    {t('admin.transactions.details.info.notes') || 'Notes'}
                  </label>
                  <p className="text-neutral-light text-sm sm:text-base">{transaction.notes}</p>
                </div>
              )}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.transactions.details.info.created') || 'Created At'}
                </label>
                <p className="text-neutral-light text-sm sm:text-base">
                  {formatDate(transaction.createdAt, 'PP', currentLanguage)}
                </p>
                <p className="text-xs text-neutral-medium mt-1">
                  {formatRelativeTime(transaction.createdAt, currentLanguage)}
                </p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.transactions.details.info.updated') || 'Updated At'}
                </label>
                <p className="text-neutral-light text-sm sm:text-base">
                  {formatDate(transaction.updatedAt, 'PP', currentLanguage)}
                </p>
                <p className="text-xs text-neutral-medium mt-1">
                  {formatRelativeTime(transaction.updatedAt, currentLanguage)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4 sm:space-y-6">
          {/* Status Card */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 sm:p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.2s both',
            }}
          >
            <h2 className="text-lg sm:text-xl font-heading font-bold text-brand-accent mb-4">
              {t('admin.transactions.details.info.status') || 'Status'}
            </h2>
            <div className="mb-4">
              <span
                className={`inline-block px-3 py-2 text-sm font-semibold rounded border ${getStatusColor(transaction.status)}`}
              >
                {t(`admin.transactions.status.${transaction.status}`) || transaction.status}
              </span>
            </div>
          </div>

          {/* Amount Card */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 sm:p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.3s both',
            }}
          >
            <h2 className="text-lg sm:text-xl font-heading font-bold text-brand-accent mb-4">
              {t('admin.transactions.details.info.amount') || 'Amount'}
            </h2>
            <div className="mb-4">
              <p className={`text-3xl sm:text-4xl font-heading font-bold ${getTypeColor(transaction.type)}`}>
                {getTypeSign(transaction.type)}${formatAmountExact(transaction.amount)}
              </p>
              <p className="text-sm text-neutral-medium mt-2 uppercase">{transaction.currency}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
